import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuy.cam';

export default function SafetyPage() {
  return (
    <>
      <SEOHead
        title="Safety & Disclaimer – OOPBuy Spreadsheet"
        description="Important information about using the OOPBuy spreadsheet — transparency commitments, disclaimers, safe shopping tips, and your responsibilities as a user. We provide informational product data only."
        keywords="oopbuy spreadsheet safety, shopping disclaimer, safe shopping tips, product guide transparency"
        canonicalUrl={`${SITE_URL}/safety-disclaimer`}
        ogTitle="Safety & Disclaimer – OOPBuy Spreadsheet"
        ogDescription="Important information about using the OOPBuy spreadsheet — transparency, disclaimers, and safe shopping tips."
        ogUrl={`${SITE_URL}/safety-disclaimer`}
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Safety & Disclaimer – OOPBuy Spreadsheet',
          description: 'Important information about using the OOPBuy spreadsheet — transparency commitments, disclaimers, safe shopping tips, and your responsibilities as a user.',
          url: `${SITE_URL}/safety-disclaimer`,
          about: {
            '@type': 'Organization',
            name: 'OOPBuy Spreadsheet',
            url: SITE_URL,
          },
        }}
      />
      <Navbar />
      <main>
        {/* ... existing code ... */}
        <article className="pt-20">
          <header className="py-12 md:py-20 bg-background-50 px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3">
                Important Information
              </p>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                Safety & Disclaimer
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                Important information about using the OOPBuy spreadsheet, your responsibilities as a user, and our commitment to transparency.
              </p>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-10">
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Our Commitment to Transparency
                </h2>

                <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8 mb-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">We Provide Information Only</h3>
                      <p className="text-sm text-foreground-600">
                        The OOPBuy spreadsheet provides informational product data. We do not sell products directly, hold inventory, or process payments. Our role is to organize and present product information to help you make informed decisions.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-user-search-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Verify Independently</h3>
                      <p className="text-sm text-foreground-600">
                        Users should independently verify product details, prices, and availability before making purchases. Product information may change, and we recommend checking current details before committing to any transaction.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-timer-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Weekly Updates</h3>
                      <p className="text-sm text-foreground-600">
                        We update the spreadsheet weekly, but individual product availability can change rapidly. Always check the latest version for the most current information.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Disclaimer
                </h2>
                <p className="mb-4">
                  The OOPBuy spreadsheet is an informational resource designed to help users discover and compare products. By using this website and the associated spreadsheet, you acknowledge and agree to the following:
                </p>
                <ul className="list-disc pl-5 space-y-3 text-sm md:text-base">
                  <li>
                    <strong>No Direct Sales:</strong> OOPBuy Spreadsheet does not sell, ship, or warranty any products listed in the spreadsheet. All product purchases are made through third-party platforms and agents.
                  </li>
                  <li>
                    <strong>Information Accuracy:</strong> While we strive to maintain accurate product information, prices, availability, and product details are subject to change without notice. We do not guarantee the accuracy of all listings at all times.
                  </li>
                  <li>
                    <strong>Third-Party Links:</strong> Our website contains links to external websites (including kaspreadsheet.com and category pages). We are not responsible for the content, policies, or practices of these third-party sites.
                  </li>
                  <li>
                    <strong>User Responsibility:</strong> Users are solely responsible for verifying product details, conducting due diligence, and making informed purchasing decisions. We recommend reviewing return policies and shipping terms before completing any purchase.
                  </li>
                  <li>
                    <strong>No Endorsement:</strong> Inclusion of a product in the OOPBuy spreadsheet does not constitute an endorsement or guarantee of quality. Product ratings reflect user feedback and are provided for reference only.
                  </li>
                  <li>
                    <strong>Shipping Variability:</strong> Shipping times, costs, and availability vary by product, destination, and agent. The shipping estimates in the spreadsheet are approximate and should not be considered guarantees.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Safe Shopping Tips
                </h2>
                <p className="mb-4">
                  To ensure a positive experience when using the OOPBuy spreadsheet, we recommend the following practices:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                    <i className="ri-check-double-line text-accent-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-foreground-700">
                      <strong>Cross-reference product details</strong> — Compare the spreadsheet information with other sources before making significant purchases.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                    <i className="ri-check-double-line text-accent-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-foreground-700">
                      <strong>Start with smaller orders</strong> — If you are new to the platform, begin with lower-value items to familiarize yourself with the process.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                    <i className="ri-check-double-line text-accent-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-foreground-700">
                      <strong>Track your shipments</strong> — Keep records of tracking numbers and expected delivery dates for all purchases.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                    <i className="ri-check-double-line text-accent-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-foreground-700">
                      <strong>Check the latest spreadsheet version</strong> — Always use the most recent update to ensure you have current pricing and availability data.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Contact & Resources
                </h2>
                <p className="mb-4">
                  For questions about specific products, purchasing processes, or shipping, please refer to the main OOPBuy platform. The spreadsheet is an informational tool, and all transactional inquiries should be directed through the appropriate channels on the main site.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Visit Main Platform
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold text-foreground-800 border-2 border-foreground-200 rounded-full hover:border-foreground-400 transition-colors whitespace-nowrap"
                  >
                    Back to Home
                    <i className="ri-home-4-line"></i>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}